import { useState, useEffect, useReducer, useMemo } from 'react';
import {
  Button,
  Col,
  Form,
  Row,
  Input,
  Select,
  Radio,
  Checkbox,
  Space,
  message,
  InputNumber,
  Switch
} from 'antd';
import {
  AudioMutedOutlined,
  AudioOutlined,
  PlusOutlined
} from '@ant-design/icons';
import CustomModal from '@/common/CustomModal';
import styles from '../Dashboard/index.module.less';
import FormItem from '@/common/FormItem';
import {
  getAllClients,
  getAllUsers,
  getAllProjects,
  createNewTask,
  selectTaskSlice,
  getSpeechmaticJWT
} from '../Tasks/redux/slice';
import { useDispatch, useSelector } from 'react-redux';
import CustomButton from '@/common/CustomButton';
import { FollowUpFormContent } from './ToggleFollow';

const { TextArea } = Input;

import {
  useAudioDevices,
  usePCMAudioListener,
  usePCMAudioRecorderContext
} from '@speechmatics/browser-audio-input-react';

import { useRef } from 'react';
import {
  useRealtimeTranscription,
  useRealtimeEventListener
} from '@speechmatics/real-time-client-react';

export const SAMPLE_RATE = 16000;

const getSpeechmaticToken = async (dispatch) => {
  const now = Date.now();

  const storedToken = window.speechToken;
  const storedExpiry = window.speechTokenExpiry;

  if (storedToken && storedExpiry && now < storedExpiry) {
    return storedToken;
  }

  try {
    // Create a loading message with a unique key to be able to close it later
    const loadingKey = 'speechmatics-loading';
    message.loading({
      content: 'Getting speech recognition ready...',
      key: loadingKey,
      duration: 0
    });

    const res = await dispatch(getSpeechmaticJWT()).unwrap();

    // Close the loading message
    message.destroy(loadingKey);

    const { token, expires_in } = res;

    if (!token || !expires_in) {
      console.error('Failed to get Speechmatics JWT or expiryTime');
      message.error('Failed to initialize speech recognition');
      return null;
    }

    const expiryTimestamp = now + expires_in * 1000;

    window.speechToken = token;
    window.speechTokenExpiry = expiryTimestamp.toString();

    return token;
  } catch (error) {
    console.error('Error fetching Speechmatics JWT:', error);
    message.error('Failed to connect to speech recognition service');
    return null;
  }
};

const BAR_COUNT = 8;
const BAR_HEIGHT = 32;

function AudioVisualizer({ analyser }) {
  const svgRef = useRef(null);

  useEffect(() => {
    if (!analyser) return;

    let frameId = undefined;
    function report() {
      if (!analyser) return;

      const binCount = analyser.frequencyBinCount;
      const data = new Uint8Array(binCount);

      analyser.getByteFrequencyData(data);

      const clippedData = data.slice(0, data.length / 2);

      const stepSize = Math.floor(clippedData.length / BAR_COUNT);
      const lineElements = svgRef.current?.querySelectorAll('line');

      for (let i = 0; i < BAR_COUNT; i++) {
        const barAmp =
          (BAR_HEIGHT *
            (data
              .slice(i * stepSize, (i + 1) * stepSize)
              .reduce((acc, curr) => acc + curr, 0) /
              stepSize)) /
          255;

        lineElements?.[i].setAttribute('y1', `${BAR_HEIGHT - barAmp}`);
        lineElements?.[i].setAttribute('y2', `${BAR_HEIGHT + barAmp}`);
      }
      frameId = requestAnimationFrame(report);
    }

    report();

    return () => {
      if (typeof frameId === 'number') {
        cancelAnimationFrame(frameId);
      }
    };
  }, [analyser]);

  if (!analyser) return null;

  return (
    <svg
      ref={svgRef}
      xmlns="http://www.w3.org/2000/svg"
      width="20px"
      height="28px"
      viewBox="0 0 64 64"
      fill="none"
    >
      <title>Audio</title>
      {new Array(BAR_COUNT).fill(null).map((_, n) => (
        <line
          // biome-ignore lint/suspicious/noArrayIndexKey: static
          key={n}
          x1={8 * n + 4}
          x2={8 * n + 4}
          y1={BAR_HEIGHT}
          y2={BAR_HEIGHT}
          stroke="#000"
          strokeOpacity="1"
          strokeWidth="4"
          strokeLinecap="round"
        />
      ))}
    </svg>
  );
}

function transcriptReducer(words, event) {
  if (event.message === 'AddTranscript') {
    return [
      ...words.filter((w) => !w.partial),
      ...event.results.map((result) => ({
        text: result.alternatives?.[0].content ?? '',
        startTime: result.start_time ?? 0,
        endTime: result.end_time ?? 0,
        punctuation: result.type === 'punctuation'
      }))
    ];
  }

  if (event.message === 'AddPartialTranscript') {
    return [
      ...words.filter((w) => !w.partial),
      ...event.results.map((result) => ({
        text: result.alternatives?.[0].content ?? '',
        startTime: result.start_time ?? 0,
        endTime: result.end_time ?? 0,
        punctuation: result.type === 'punctuation',
        partial: true
      }))
    ];
  }

  if (event.message === 'ClearTranscript') {
    return [];
  }

  return words;
}

function RecordingButton({
  deviceId,
  analyser,
  isRecording,
  onStart,
  onStop,
  isDisabled,
  isLoading
}) {
  if (isRecording) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px'
        }}
      >
        <AudioVisualizer analyser={analyser} />
        <AudioOutlined style={{ color: 'red' }} onClick={onStop} />
      </div>
    );
  } else if (isLoading) {
    return (
      <div className="recording-button-loading">
        <div className="dot-pulse"></div>
      </div>
    );
  } else {
    return (
      <AudioOutlined
        style={{ color: '#0653fa' }}
        onClick={() => !isDisabled && onStart()}
        disabled={isDisabled}
      />
    );
  }
}

function MicrophoneSelect({ setDeviceId, renderGrantedComponent }) {
  const devices = useAudioDevices();
  switch (devices.permissionState) {
    case 'prompt':
      return (
        <AudioOutlined
          style={{ color: '#0653fa' }}
          onClick={devices.promptPermissions}
        />
      );
    case 'prompting':
      return <AudioMutedOutlined />;
    case 'granted': {
      setDeviceId(devices.deviceList[0]?.deviceId);
      return renderGrantedComponent;
    }
    case 'denied':
      return (
        <AudioOutlined
          style={{ color: '#0653fa' }}
          onClick={() =>
            alert(
              'Microphone access denied. Please enable it in your browser settings (lock icon → Microphone → Allow).'
            )
          }
        />
      );
    default:
      devices;
      return null;
  }
}
const FloatingActionButton = (props) => {
  const [showTaskModel, setShowModel] = useState(false);
  const [isFollowUpMode, setIsFollowUpMode] = useState(false);
  const [clients, setClients] = useState('');
  const [, setPriority] = useState('');
  const [checked, setChecked] = useState(false);
  const [team, setTeam] = useState([]);
  const [projects, setProjects] = useState([]);
  const [search, setSearch] = useState('');
  const [targetField, setTargetField] = useState('');
  const [isLoadingRecording, setIsLoadingRecording] = useState(false);
  const [transcription, dispatchFn] = useReducer(transcriptReducer, []);

  const { analyser, startRecording, stopRecording, isRecording, audioContext } =
    usePCMAudioRecorderContext();
  const { startTranscription, stopTranscription, sendAudio, socketState } =
    useRealtimeTranscription();
  const [deviceId, setDeviceId] = useState('');
  usePCMAudioListener((audio) => {
    if (socketState === 'open') {
      sendAudio(audio);
    }
  });

  useRealtimeEventListener('receiveMessage', (e) => {
    dispatchFn(e.data);
  });

  //   const [form] = Form.useForm();
  const [form] = Form.useForm();
  const { loading } = useSelector(selectTaskSlice);
  const dispatch = useDispatch();
  const showModal = () => {
    setShowModel(true);
  };

  const onSearch = (e) => {
    setSearch(e);
  };

  const getDefaultInputs = () => {
    if (!props.taskId) {
      form.setFieldsValue({ is_priority: 'no' });
    }
    handleGetAllUsers();
    setProjects([]);
    setSearch('');
    setChecked(false);
  };

  useEffect(() => {
    getDefaultInputs();
  }, [showTaskModel]);

  useEffect(() => {
    if (props.taskId) {
      setShowModel(true);
      if (props.taskData) {
        const data = props.taskData;
        form.setFieldsValue({
          ...data,
          is_priority: data.priority ? 'yes' : 'no'
        });
        //adding here
        setChecked(data?.is_task_assigned);
        if (data?.is_task_assigned && data?.user_id) {
          form.setFieldsValue({ user_id: data.user_id });
        }

        //closing her
        handleGetAllClients();
        handleGetAllUsers();
        handleGetAllProjects(data.client_id);
        setSearch('');
        form.setFieldsValue({
          project_id: data.project_id,
          client_id: data.client_id
        });
      }
    }
  }, [props.taskId]);

  useEffect(() => {
    handleGetAllClients();
    handleGetAllUsers();
    // handleGetAllProjects();
  }, [search]);

  const handleClose = () => {
    form.resetFields();
    form.setFieldsValue({ is_priority: 'no' });
    setSearch('');
    setChecked(false);
    setIsFollowUpMode(false);
    if (props.setTaskId) {
      props.setTaskId(null);
    }
    setShowModel(false);
    handleStopRecording();
  };

  const handleGetAllClients = () => {
    const payload = { search };
    dispatch(getAllClients(payload)).then((res) => {
      if (res?.payload?.success) {
        setClients(res?.payload?.data);
      }
    });
  };

  const handleGetAllProjects = (client_id) => {
    const payload = { client_id };
    dispatch(getAllProjects(payload)).then((res) => {
      if (res?.payload?.success) {
        setProjects(res?.payload?.data);
      }
    });
  };

  const handleGetAllUsers = () => {
    const payload = { search: '' };
    dispatch(getAllUsers(payload)).then((res) => {
      if (res?.payload?.success) {
        setTeam(res?.payload?.data);
      }
    });
  };

  const filteredTeam = useMemo(() => {
    return team?.filter(
      (member) =>
        !member.on_leave &&
        (!member.staff_augmentation ||
          Number(member.staff_augmentation_percentage || 0) < 100)
    );
  }, [team]);

  const handlePriority = (e) => {
    setPriority(e.target.value);
  };

  const onChange = (e) => {
    setChecked(e.target.checked);
  };
  const handleSubmit = () => {
    props.handleGetAllTasks();
  };

  // Helper function to properly stop current session
  const stopCurrentSession = async () => {
    if (isRecording && targetField) {
      try {
        // Save current transcription to the field
        // const transcriptionText = transcription
        //   .map(({ text, punctuation }) => `${!punctuation ? ' ' : ''}${text}`)
        //   .join('')
        //   .trim();

        // if (transcriptionText) {
        //   const currentValue = form.getFieldValue(targetField) || '';
        //   const newValue = currentValue
        //     ? `${currentValue} ${transcriptionText}`
        //     : transcriptionText;

        //   form.setFieldsValue({
        //     [targetField]: newValue
        //   });
        // }

        // Stop recording and transcription
        stopRecording();
        await stopTranscription();

        // Clear transcription state
        dispatchFn({ message: 'ClearTranscript' });
        setTargetField('');

        // Wait a moment for cleanup
        await new Promise((resolve) => setTimeout(resolve, 100));
      } catch (error) {
        console.error('Error stopping current session:', error);
        // Force cleanup even if there's an error
        try {
          stopRecording();
          await stopTranscription();
        } catch (cleanupError) {
          console.error('Error in forced cleanup:', cleanupError);
        }
        dispatchFn({ message: 'ClearTranscript' });
        setTargetField('');
      }
    }
  };

  const handleStartRecording = async (field) => {
    try {
      // If already recording for a different field, stop it first
      if (isRecording && targetField !== field) {
        await stopCurrentSession();
      }

      // Check if we have proper audio context and device
      if (!audioContext) {
        message.error('Audio context not available');
        return;
      }

      if (!deviceId) {
        message.error('No microphone device selected');
        return;
      }

      setIsLoadingRecording(true);
      setTargetField(field);
      dispatchFn({ message: 'ClearTranscript' });

      const sampleRate = audioContext.sampleRate || SAMPLE_RATE;

      // Get JWT token
      const jwt = await getSpeechmaticToken(dispatch);
      if (!jwt) {
        setIsLoadingRecording(false);
        setTargetField('');
        return;
      }

      // Start transcription first
      await startTranscription(jwt, {
        transcription_config: {
          language: 'en',
          enable_partials: true
        },
        audio_format: {
          type: 'raw',
          encoding: 'pcm_f32le',
          sample_rate: sampleRate
        }
      });

      // Then start recording
      await startRecording({
        audioContext,
        deviceId,
        sampleRate: sampleRate,
        channels: 1
      });

      setIsLoadingRecording(false);
    } catch (error) {
      console.error('Error starting recording:', error);

      // Handle specific quota exceeded error
      if (
        error.message === 'quota_exceeded' ||
        error.message?.includes('Concurrent Quota Exceeded')
      ) {
        message.error(
          'Multiple recording sessions detected. Please try again in a moment.'
        );
        // Force cleanup and retry after a delay
        await stopCurrentSession();
        setTimeout(() => {
          handleStartRecording(field);
        }, 1000);
      } else {
        message.error(
          'Failed to start recording: ' + (error.message || 'Unknown error')
        );
      }

      setTargetField('');
      setIsLoadingRecording(false);
    }
  };

  const handleStopRecording = async () => {
    await stopCurrentSession();
  };

  // Update form field in real-time during recording
  useEffect(() => {
    if (isRecording && transcription.length > 0 && targetField) {
      const transcriptionText = transcription
        .map(({ text, punctuation }) => `${!punctuation ? ' ' : ''}${text}`)
        .join('')
        .trim();

      if (transcriptionText) {
        form.setFieldsValue({
          [targetField]: transcriptionText
        });
      }
    }
  }, [transcription, isRecording, targetField, form]);

  // Clean up recording when component unmounts
  useEffect(() => {
    return () => {
      if (isRecording) {
        try {
          stopRecording();
          stopTranscription();
        } catch (error) {
          console.error('Error cleaning up recording:', error);
        }
      }
    };
  }, []);

  const handleFormValuesChange = (changedVal, val) => {
    if (changedVal.client_id) {
      form.setFieldsValue({ project_id: 'Select project' });
      handleGetAllProjects(val.client_id);
    }
  };

  const onFinish = (values) => {
    if (isRecording) {
      handleStopRecording();
    }
    values['priority'] = values?.is_priority === 'yes' ? true : false;
    values['is_task_assigned'] = checked ? true : false;
    values['description'] = values['description'] ? values['description'] : '';
    if (props.taskId) {
      values['id'] = props.taskId;
    }
    if (!checked) {
      delete values.user_id; // prevent sending user_id when not assigned
    }

    dispatch(createNewTask(values)).then((res) => {
      if (res?.payload?.success) {
        form.resetFields();
        form.setFieldsValue({ is_priority: 'no' });
        setChecked(false);
        handleGetAllClients();
        setProjects([]);
        setSearch('');
        if (props.setTaskId) {
          props.setTaskId(null);
        }
        if (typeof props.handleGetAllTasks === 'function') {
          props.handleGetAllTasks();
        }
        setShowModel(false);
      }
    });
  };

  return (
    <>
      <Button
        type="primary"
        shape="circle"
        icon={<PlusOutlined />}
        size="large"
        onClick={showModal}
        style={{
          position: 'fixed',
          bottom: 24,
          right: 34,
          width: 40,
          height: 40,
          display: props.taskId ? 'none' : 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 6px 16px -8px rgba(0, 0, 0, 0.2)',
          zIndex: 1000
        }}
      />

      {/* Task Creation Modal */}
      {showTaskModel ? (
        <CustomModal
          // title="Create Department"
          visible={showTaskModel}
          footer={null}
          className={`!w-full sm:!w-[60%] md:!w-[50%] lg:!w-[45%] xl:!w-[40%] ${styles.customModal}`}
          closable={false}
        >
          {!props.taskId && (
            <div className="flex items-center justify-start gap-2 mb-2 pb-2 border-b border-gray-200">
              <span className="text-sm text-gray-600">Task</span>
              <Switch
                size="small"
                checked={isFollowUpMode}
                onChange={setIsFollowUpMode}
                // checkedChildren="Follow-up"
                // unCheckedChildren="Task"
              />
              <span className="text-sm text-gray-600">Follow-up</span>
            </div>
          )}

          {isFollowUpMode ? (
            <FollowUpFormContent onClose={handleClose} />
          ) : (
            <Form
              form={form}
              layout="vertical"
              onFinish={onFinish}
              onValuesChange={handleFormValuesChange}
            >
              <h3 className={`${styles.mainTitle} mb-3`}>
                {props.taskId ? 'Edit task' : `Create a task`}
              </h3>

              <Row gutter={24}>
                <Col span={24} style={{ position: 'relative' }}>
                  <FormItem
                    label="Title"
                    name="title"
                    rules={[{ required: true, message: 'Title is required' }]}
                    className={styles.customLable}
                  >
                    <Input allowClear placeholder="Enter title" />
                  </FormItem>

                  <div
                    style={{
                      position: 'absolute',
                      right: 0,
                      top: 0,
                      paddingRight: '12px'
                    }}
                  >
                    <MicrophoneSelect
                      setDeviceId={setDeviceId}
                      renderGrantedComponent={
                        <RecordingButton
                          deviceId={deviceId}
                          analyser={analyser}
                          isRecording={isRecording && targetField === 'title'}
                          // isDisabled={isRecording}
                          isLoading={
                            isLoadingRecording && targetField === 'title'
                          }
                          onStart={() => handleStartRecording('title')}
                          onStop={() => handleStopRecording('title')}
                        />
                      }
                    />
                  </div>
                </Col>
                <Col span={24} style={{ position: 'relative' }}>
                  <FormItem
                    label="Description"
                    name="description"
                    rules={[{ required: false }]}
                    className={styles.customLable}
                  >
                    <TextArea
                      allowClear
                      rows={4}
                      placeholder="Enter description"
                    />
                  </FormItem>
                  <div
                    style={{
                      position: 'absolute',
                      right: 0,
                      top: 0,
                      paddingRight: '12px'
                    }}
                  >
                    <MicrophoneSelect
                      setDeviceId={setDeviceId}
                      renderGrantedComponent={
                        <RecordingButton
                          deviceId={deviceId}
                          analyser={analyser}
                          isRecording={
                            isRecording && targetField === 'description'
                          }
                          // isDisabled={isRecording}
                          isLoading={
                            isLoadingRecording && targetField === 'description'
                          }
                          onStart={() => handleStartRecording('description')}
                          onStop={() => handleStopRecording('description')}
                        />
                      }
                    />
                  </div>
                </Col>
                <Col span={12}>
                  <FormItem
                    label="Client"
                    name="client_id"
                    rules={[{ required: true, message: 'Client is required' }]}
                    className={styles.customLable}
                  >
                    <Select
                      showSearch
                      placeholder="All Clients"
                      optionFilterProp="children"
                      defaultValue="Select client"
                      style={{ width: '100%' }}
                      className={styles.selectField}
                      onSearch={onSearch}
                      // options={clients}
                    >
                      {clients?.length
                        ? clients?.map((item, index) => {
                            return (
                              <option value={item?.id} key={index}>
                                {item?.client_name}
                              </option>
                            );
                          })
                        : ''}
                    </Select>
                  </FormItem>
                </Col>
                <Col span={12}>
                  <FormItem
                    label="Project"
                    name="project_id"
                    rules={[
                      { required: true, message: 'Project is required' },
                      {
                        validator: (_, value) =>
                          value && value === 'Select project'
                            ? Promise.reject('Project is required')
                            : Promise.resolve()
                      }
                    ]}
                    className={styles.customLable}
                  >
                    <Select
                      showSearch
                      placeholder="All Projects"
                      optionFilterProp="children"
                      defaultValue="Select project"
                      style={{ width: '100%' }}
                      className={styles.selectField}
                      onSearch={onSearch}
                      // options={clients}
                    >
                      {projects?.length
                        ? projects?.map((item, index) => {
                            return (
                              <option value={item?.id} key={index}>
                                {item?.project}
                              </option>
                            );
                          })
                        : ''}
                    </Select>
                  </FormItem>
                </Col>
                {checked ? (
                  <Col span={24} style={{ position: 'relative' }}>
                    <FormItem
                      label="Estimated hours"
                      name="hours"
                      className={styles.customLable}
                      rules={[
                        {
                          validator: (_, value) => {
                            if (value === undefined || value === null) {
                              return Promise.resolve();
                            }

                            if (value > 10) {
                              return Promise.reject(
                                new Error('Estimated hours cannot exceed 10')
                              );
                            }

                            return Promise.resolve();
                          }
                        }
                      ]}
                    >
                      <InputNumber
                        style={{ width: '100%' }}
                        min={0}
                        step={0.5}
                        precision={2}
                        placeholder="e.g. 3"
                        controls={false}
                        onKeyDown={(e) => {
                          const allowedKeys = [
                            'Backspace',
                            'Delete',
                            'ArrowLeft',
                            'ArrowRight',
                            'Tab'
                          ];

                          if (
                            allowedKeys.includes(e.key) ||
                            /^[0-9.]$/.test(e.key)
                          ) {
                            return;
                          }

                          e.preventDefault();
                        }}
                        onPaste={(e) => {
                          const pasted = e.clipboardData.getData('text');
                          if (!/^\d*\.?\d*$/.test(pasted)) {
                            e.preventDefault();
                          }
                        }}
                      />
                    </FormItem>

                    <div
                      style={{
                        position: 'absolute',
                        right: 0,
                        top: 0,
                        paddingRight: '12px'
                      }}
                    ></div>
                  </Col>
                ) : (
                  ''
                )}
                {/* <Col span={12}>
                <FormItem
                  label="Priority"
                  name="is_priority"
                  rules={[{ required: true }]}
                  className={styles.customLable}
                >
                  <Radio.Group
                    buttonStyle="solid"
                    size="small"
                    onChange={(e) => handlePriority(e)}
                  >
                    <Radio value="yes" className={styles.projectText}>
                      Yes
                    </Radio>
                    <Radio value="no" className={styles.projectText}>
                      No
                    </Radio>
                  </Radio.Group>
                </FormItem>
              </Col> */}
                {/* <Col span={12} hidden={props.taskId}> */}
                <Col span={12}>
                  <FormItem
                    label=" "
                    name="is_task_assigned"
                    rules={[{ required: false }]}
                    className={styles.customLable}
                  >
                    <Checkbox checked={checked} onChange={onChange}>
                      Assign immediately
                    </Checkbox>
                  </FormItem>
                </Col>
                {checked ? (
                  <Col span={12} className="!mt-2">
                    <FormItem
                      label="Select team member"
                      name="user_id"
                      rules={[
                        { required: true, message: 'Team member is required' }
                      ]}
                      className={styles.customLable}
                    >
                      {/* <Select
                      showSearch
                      placeholder="All Clients"
                      optionFilterProp="children"
                      defaultValue="Select member"
                      style={{ width: '100%' }}
                      className={styles.selectField}
                      onSearch={onSearch}
                      // options={clients}
                    >
                      {team?.length
                        ? team?.map((item, index) => {
                            return (
                              <option value={item?.id} key={index}>
                                {item?.first_name} {item?.last_name}
                              </option>
                            );
                          })
                        : ''}
                    </Select> */}
                      <Select
                        showSearch
                        placeholder="Select member"
                        optionFilterProp="children"
                        style={{ width: '100%' }}
                        className={styles.selectField}
                        onSearch={onSearch}
                      >
                        {filteredTeam?.map((item) => (
                          <Select.Option value={item.id} key={item.id}>
                            {item.first_name} {item.last_name}
                          </Select.Option>
                        ))}
                      </Select>
                    </FormItem>
                  </Col>
                ) : (
                  ''
                )}
              </Row>
              <br />
              <FormItem className="text-center">
                <Space>
                  <CustomButton
                    className={styles.inwardButton}
                    onClick={() => handleClose()}
                    type="primary"
                    ghost
                  >
                    Close
                  </CustomButton>
                  <CustomButton
                    className={styles.inwardButton}
                    htmlType="submit"
                    type="primary"
                    onClick={handleSubmit}
                    loading={loading}
                  >
                    {props.taskId ? 'Update' : 'Create'}
                  </CustomButton>
                </Space>
              </FormItem>
            </Form>
          )}
        </CustomModal>
      ) : (
        ''
      )}
    </>
  );
};

// Add CSS for the loading animation
const loadingStyles = `
  .recording-button-loading {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    padding-right: 12px;
  }
  
  .dot-pulse {
    position: relative;
    width: 8px;
    height: 8px;
    border-radius: 4px;
    background-color: #0653fa;
    color: #0653fa;
    animation: dotPulse 1.5s infinite linear;
  }
  
  .dot-pulse::before, .dot-pulse::after {
    content: '';
    display: inline-block;
    position: absolute;
    top: 0;
    width: 8px;
    height: 8px;
    border-radius: 4px;
    background-color: #0653fa;
    color: #0653fa;
  }
  
  .dot-pulse::before {
    left: -12px;
    animation: dotPulseBefore 1.5s infinite linear;
  }
  
  .dot-pulse::after {
    left: 12px;
    animation: dotPulseAfter 1.5s infinite linear;
  }
  
  @keyframes dotPulseBefore {
    0% {
      transform: scale(0);
    }
    25% {
      transform: scale(0.5);
    }
    50% {
      transform: scale(1);
    }
    75% {
      transform: scale(0.5);
    }
    100% {
      transform: scale(0);
    }
  }
  
  @keyframes dotPulse {
    0% {
      transform: scale(0.5);
    }
    25% {
      transform: scale(1);
    }
    50% {
      transform: scale(0.5);
    }
    75% {
      transform: scale(1);
    }
    100% {
      transform: scale(0.5);
    }
  }
  
  @keyframes dotPulseAfter {
    0% {
      transform: scale(1);
    }
    25% {
      transform: scale(0.5);
    }
    50% {
      transform: scale(0);
    }
    75% {
      transform: scale(0.5);
    }
    100% {
      transform: scale(1);
    }
  }
`;

// Add the styles to the document head
if (typeof document !== 'undefined') {
  const styleEl = document.createElement('style');
  styleEl.type = 'text/css';
  styleEl.appendChild(document.createTextNode(loadingStyles));
  document.head.appendChild(styleEl);
}

export default FloatingActionButton;
